import { CloseOutlined, CopyOutlined, DeleteOutlined } from '@ant-design/icons';
import { Col, Row, Space } from 'antd';
import { Components } from '@fasstech/flexiget';
import { allPass,
  always,
  append,
  compose,
  cond,
  equals,
  filter,
  head,
  isNil,
  length,
  path,
  pathEq,
  prop,
  propOr,
  propSatisfies,
  T,
  toLower,
  isNotNil } from 'ramda';
import { propNotEq } from 'ramda-adjunct';
import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useShowAction } from '../../../hooks/useShowAction';
import { useTreeContext } from '../../../components/treeStructure/treeContext';
import { deleteMutationMessage, errorMessage, successMessage } from '../../../utils/messageMutation';
import CreateWidgetMutation from '../../../_graphql/mutations/documents/CreateWidgetMutation';
import DeleteWidgetMutation from '../../../_graphql/mutations/documents/DeleteWidgetMutation';
import { renderDate } from '../../docs/components/helpers/dates';
import { updateKeys } from '../../../components/treeStructure/treeHelper';
import ConfirmButton from '../../../components/ConfirmButton';
import useActionGuard from '../../../hooks/useActionGuard';
import useOfferRole from '../../../hooks/useOfferRole';
import { canEditOfferItem } from '../../../components/users/utils/roles';
import DocumentDates from './documentsSection/DocumentDates';

const WidgetTreeRow = ({
  folder,
  elementId,
  refRow,
  offerIsDraft,
  scope,
  args,
  setWidgets,
  isPublished,
  widgets,
  setIsLoading,
  setRefetchWidgets
}) => {
  const elementRef = useRef();
  const { showAction } = useShowAction(elementRef);

  const { offerId } = args;
  const { removeTree, expandedKeys, setExpandedKeys, trees } = useTreeContext();
  const element = prop('element', folder);
  const elementName = prop('name', element);
  const isUsed = propOr(false, 'isUsed', element);
  const { userIsAdmin, userIsOfferEditor } = useOfferRole(offerId);
  const userCanEditWidgets = canEditOfferItem({
    userIsAdmin, userIsOfferEditor, scope, isDraft: offerIsDraft
  });
  const { loading, run } = useActionGuard();

  const handleDeleteWidget = () => {
    run(`delete-widget-${elementId}`, (done) => {
      if (isNil(prop('treeId', element))) {
        const nodeKey = compose(
          path([0, 'key']),
          filter(pathEq(elementId, ['element', 'elmId']))
        )(trees);

        const updatedKeys = updateKeys(expandedKeys, [nodeKey], 'remove');

        setExpandedKeys(updatedKeys);
        DeleteWidgetMutation({ widgetId: elementId }, (ok, error) => {
          deleteMutationMessage(ok, error, 'widget', setWidgets(filter(propNotEq(elementId, 'id'))));
          done(ok, error);
        });
      } else {
        removeTree(prop('treeId', element), folder);
        DeleteWidgetMutation({ widgetId: prop('elmId', element) }, (ok, error) => {
          deleteMutationMessage(ok, error, 'widget', setWidgets(filter(propNotEq(prop('elmId', element), 'id'))));
          setRefetchWidgets(true);
          done(ok, error);
        });
      }
    });
  };

  const handleDeleteWidgetFromDirectory = () => {
    removeTree(prop('treeId', element), folder, true);
  };

  const handleDuplicate = () => {
    setIsLoading(true);
    const widgetsHasSameRootWidgetId = filter(w => equals(prop('id', element), prop('rootWidgetId', w)))(widgets);
    const lengthSameParentWidget = length(widgetsHasSameRootWidgetId);
    const duplicateWidget = {
      name: `${prop('name', element)} (${lengthSameParentWidget + 1})`,
      nbColsGrid: prop('nbColsGrid', element),
      heightRowsGrid: prop('heightRowsGrid', element),
      contents: prop('contents', element),
      orientation: propOr('portrait', 'orientation', element),
      scope,
      offerId,
      frameStyle: prop('frameStyle', element),
      rootWidgetId: prop('id', element)
    };

    const nodeKey = compose(
      prop('key'),
      head,
      filter(pathEq(true, ['element', 'isFolder']))
    )(trees);

    const updatedKeys = updateKeys(expandedKeys, [nodeKey], 'duplicate');
    setExpandedKeys(updatedKeys);
    run(`duplicate-widget-${prop('id', element)}`, (done) => CreateWidgetMutation({ widget: duplicateWidget }, (ok, error, id) => {
      if (ok && !error) {
        successMessage('widget', 'dupliqué');
        setWidgets(append({ id, ...duplicateWidget }));
      } else {
        errorMessage();
      }
      setIsLoading(false);
      done(ok, error, id);
    }));
  };

  const hasParentFolder = allPass([
    propSatisfies(isNotNil, 'parent'),
    always(prop('elmId', element))
  ])(folder);
  const widgetUrl = cond([
    [() => userCanEditWidgets, always(`/offre/${offerId}/widgets/${prop('elmId', element)}/editer`)],
    [T, always(`/offre/${offerId}/widgets/${prop('elmId', element)}/preview`)]
  ]);

  const displayActions = !isPublished && userCanEditWidgets;

  return (
    <>
      <Col ref={refRow}>
        <Link to={widgetUrl}>{elementName}</Link>
      </Col>
      <Col ref={elementRef}>
        <Row gutter={6} className={`opacity-${showAction ? '100' : '0'}`}>
          {displayActions && (
            <Col>
              <Space>
                {hasParentFolder && (
                  <Components.Tooltip
                    title="Retirer le widget de l'arborescence"
                  >
                    <CloseOutlined
                      onClick={handleDeleteWidgetFromDirectory}
                      className="text-black hover:text-flexi-grey btn-remove-item-root"
                    />
                  </Components.Tooltip>
                )}

                {userCanEditWidgets && (
                  <Components.Tooltip title="Dupliquer le widget">
                    <CopyOutlined
                      onClick={() => handleDuplicate()}
                      className="btn-duplicate-widget text-black hover:text-flexi-grey"
                    />
                  </Components.Tooltip>
                )}
                <Components.Tooltip
                  title={
                    isUsed
                      ? 'Ce widget est utilisé dans un document, vous ne pouvez pas le supprimer.'
                      : 'Supprimer le widget'
                  }
                >
                  <ConfirmButton
                    title="Êtes-vous sûr de vouloir supprimer ce widget ?"
                    onConfirm={handleDeleteWidget}
                    disabled={isUsed}
                    icon={<DeleteOutlined className={`${toLower(elementName)} btn-remove-widget hover:text-flexi-grey`}/>}
                    buttonType="text"
                    sizeButton="small"
                    className="!w-3.5 !h-3.5"
                    loading={propOr(false, `delete-widget-${elementId}`, loading)}
                  />
                </Components.Tooltip>
              </Space>
            </Col>
          )}

          {prop('dates', element) && (
            <DocumentDates renderDate={renderDate} element={element} />
          )}
        </Row>
      </Col>
    </>
  );
};

export default WidgetTreeRow;
